import type { Client } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { withReactiveResponse } from '@warp-drive/core/request';
import type { RequestInfo } from '@warp-drive/core/types/request';

import { queryClients } from '../../data/builders/system/clients.ts';

export type SearchClientsState = {
  offset: number;
  limit: number;
  title: string;
  subscription: string;
  view: string;
  domain: string;
  connection: string;
  username: string;
  expires: string;
  sortField: string;
};

interface SearchClientsSignature {
  Element: HTMLDivElement;
  Args: {
    state: Readonly<SearchClientsState>;
    setState: (state: Partial<SearchClientsState>) => void;
  };
  Blocks: {
    query: [RequestInfo<ReactiveDataDocument<Client[]>>];
  };
}

export class SearchClients extends Component<SearchClientsSignature> {
  @service declare store: Store;

  get query() {
    return withReactiveResponse<Client[]>(
      queryClients({
        title: this.args.state.title,
        subscription: this.args.state.subscription,
        view: this.args.state.view,
        domain: this.args.state.domain,
        connection: this.args.state.connection,
        username: this.args.state.username,
        expires: this.args.state.expires,
        sortField: this.args.state.sortField || 'title',
        offset: this.args.state.offset || 0,
        limit: this.args.state.limit || 30,
      })
    );
  }

  updateTextFilter =
    (field: 'title' | 'domain' | 'connection' | 'username') =>
    (event: Event): void => {
      const target = event.target as HTMLInputElement;
      this.args.setState({
        [field]: target.value,
        offset: 0,
      });
    };

  updateSelectFilter =
    (field: 'subscription' | 'view' | 'expires' | 'sortField') =>
    (event: Event): void => {
      const target = event.target as HTMLSelectElement;
      this.args.setState({
        [field]: target.value,
        offset: 0,
      });
    };

  <template>
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label for="filter-title" class="form-label">Title</label>
            <input
              type="text"
              id="filter-title"
              class="form-control"
              value={{@state.title}}
              {{debounce (this.updateTextFilter "title")}}
              placeholder="Search by client title..."
            />
          </div>
          <div class="col-md-4">
            <label for="filter-expires" class="form-label">Subscription</label>
            <select
              id="filter-expires"
              class="form-select"
              {{selectValue @state.expires ""}}
              {{on "change" (this.updateSelectFilter "expires")}}
            >
              <option value="">All</option>
              <option value="future">Active (not expired)</option>
              <option value="days-7">Expires within 7 days</option>
              <option value="past">Expired</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="filter-view" class="form-label">Product</label>
            <select
              id="filter-view"
              class="form-select"
              {{selectValue @state.view ""}}
              {{on "change" (this.updateSelectFilter "view")}}
            >
              <option value="">All</option>
              <option value="va-program">Xceed + Leadership Live</option>
              <option value="program">Xceed only</option>
              <option value="va">Leadership Live only</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="filter-domain" class="form-label">Email Domain</label>
            <input
              type="text"
              id="filter-domain"
              class="form-control"
              value={{@state.domain}}
              {{debounce (this.updateTextFilter "domain")}}
              placeholder="Search by domain..."
            />
          </div>
          <div class="col-md-4">
            <label for="filter-connection" class="form-label">Enterprise
              Connection</label>
            <input
              type="text"
              id="filter-connection"
              class="form-control"
              value={{@state.connection}}
              {{debounce (this.updateTextFilter "connection")}}
              placeholder="Search by connection..."
            />
          </div>
          <div class="col-md-4">
            <label for="sort" class="form-label">Sort by</label>
            <select
              id="sort"
              class="form-select"
              {{selectValue @state.sortField ""}}
              {{on "change" (this.updateSelectFilter "sortField")}}
            >
              <option value="title">Title</option>
              <option value="-modified">Modified (newest)</option>
              <option value="-expires">Expiry (newest)</option>
              <option value="expires">Expiry (oldest)</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    {{yield this.query to="query"}}
  </template>
}
